import React, { Component, ErrorInfo, ReactNode } from 'react';
import './GlobalErrorBoundary.css';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
    detailsVisible: boolean;
}

const CHUNK_RELOAD_KEY = 'avm:chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 15_000;

// Признаки ошибки из-за устаревших чанков после деплоя
const isChunkLoadError = (error: unknown): boolean => {
    const candidate = error as { message?: unknown; name?: unknown } | null | undefined;
    const msg = String(candidate?.message ?? '');
    const name = String(candidate?.name ?? '');
    return (
        msg.includes("Unexpected token '<'") ||
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Importing a module script failed') ||
        msg.includes('ChunkLoadError') ||
        name === 'ChunkLoadError'
    );
};

const recoverFromChunkLoadError = (): boolean => {
    try {
        const previousAttempt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
        const now = Date.now();
        if (previousAttempt && now - previousAttempt < CHUNK_RELOAD_COOLDOWN_MS) return false;
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
        window.location.reload();
        return true;
    } catch {
        return false;
    }
};

class GlobalErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            detailsVisible: false
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        // При смене релиза приложение переходит на актуальный index.html один раз.
        if (isChunkLoadError(error)) {
            if (recoverFromChunkLoadError()) {
                return { hasError: false, error: null };
            }
        }
        return { hasError: true, error };
    }

    componentDidMount() {
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            if (!isChunkLoadError(event.reason)) return;
            if (recoverFromChunkLoadError()) event.preventDefault();
        };
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        this.chunkRecoveryTimer = window.setTimeout(() => {
            sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        }, CHUNK_RELOAD_COOLDOWN_MS);
        this.removeUnhandledRejectionListener = () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    }

    componentWillUnmount() {
        window.clearTimeout(this.chunkRecoveryTimer);
        this.removeUnhandledRejectionListener?.();
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({ errorInfo });
        console.error('Uncaught error:', error, errorInfo);
    }

    handleReload = () => {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        window.location.reload();
    };

    handleGoHome = () => {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        // Hard reload на главную — сбрасывает кэш JS-чанков
        window.location.href = '/';
    };

    toggleDetails = () => {
        this.setState(prev => ({ detailsVisible: !prev.detailsVisible }));
    };

    private chunkRecoveryTimer?: number;
    private removeUnhandledRejectionListener?: () => void;

    render() {
        if (this.state.hasError) {
            return (
                <div className="error-boundary-container">
                    <div className="eb-bg-glow"></div>
                    <div className="eb-bg-glow secondary"></div>

                    <div className="eb-card">
                        <div className="eb-glitch-wrapper">
                            <div className="eb-glitch-icon">
                                <i className="fas fa-microchip"></i>
                                <div className="eb-glitch-overlay"></div>
                            </div>
                        </div>

                        <h1 className="eb-title">Упс! Что-то пошло не так</h1>
                        <p className="eb-subtitle">
                            Попробуйте полностью обновить страницу: нажмите Ctrl + F5.
                            Если ошибка повторится - напишите Мише.
                        </p>

                        <div className="eb-actions">
                            <button type="button" className="eb-btn primary" onClick={this.handleGoHome}>
                                <i className="fas fa-home"></i>
                                На главную
                            </button>
                            <button type="button" className="eb-btn secondary" onClick={this.handleReload}>
                                <i className="fas fa-redo"></i>
                                Перезагрузить
                            </button>
                        </div>

                        <div className="eb-details-section">
                            <button type="button" className="eb-details-toggle" onClick={this.toggleDetails}>
                                {this.state.detailsVisible ? 'Скрыть детали' : 'Показать детали для разработчиков'}
                                <i className={`fas fa-chevron-${this.state.detailsVisible ? 'up' : 'down'}`}></i>
                            </button>

                            {this.state.detailsVisible && (
                                <div className="eb-details-content">
                                    <div className="eb-error-message">
                                        <strong>Error:</strong> {this.state.error?.toString()}
                                    </div>
                                    <pre className="eb-error-stack">
                                        {this.state.errorInfo?.componentStack}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default GlobalErrorBoundary;
